import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, ExternalLink, ShieldAlert, ShieldCheck, ShieldQuestion } from "lucide-react";
import { toast } from "sonner";

function LevelBadge({ level }) {
  const map = {
    malicious: {
      cls: "bg-red-500/10 text-red-400 border-red-500/30",
      icon: ShieldAlert,
      label: "MALICIOSO",
    },
    suspicious: {
      cls: "bg-amber-500/10 text-amber-400 border-amber-500/30",
      icon: ShieldQuestion,
      label: "SOSPECHOSO",
    },
    safe: {
      cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
      icon: ShieldCheck,
      label: "SEGURO",
    },
  };
  const m = map[level] || map.safe;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm border ${m.cls}`}
    >
      <Icon size={11} strokeWidth={2.5} />
      {m.label}
    </span>
  );
}

export default function URLScans() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ total: 0, malicious: 0, suspicious: 0, safe: 0 });
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("all");
  const [source, setSource] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page_size: "100" });
      if (level !== "all") params.set("level", level);
      if (source !== "all") params.set("source", source);
      if (q) params.set("q", q);
      const r = await api.get(`/url-scans?${params.toString()}`);
      setItems(r.data.items);
      setTotal(r.data.total);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error("Error al cargar escaneos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      load();
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, source]);

  const onDelete = async (id) => {
    if (!window.confirm("¿Borrar este registro de escaneo?")) return;
    try {
      await api.delete(`/url-scans/${id}`);
      toast.success("Escaneo eliminado");
      load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const onClearAll = async () => {
    const scope = level === "all" ? "TODOS los escaneos" : `los escaneos "${level.toUpperCase()}"`;
    if (!window.confirm(`¿Seguro que quieres borrar ${scope}? Esta acción no se puede deshacer.`)) return;
    try {
      const params = new URLSearchParams();
      if (level !== "all") params.set("level", level);
      const r = await api.delete(`/url-scans?${params.toString()}`);
      toast.success(`Eliminados ${r.data.deleted} registros`);
      load();
    } catch (e) {
      toast.error("No se pudo limpiar");
    }
  };

  const Stat = ({ label, value, color }) => (
    <div className="border border-zinc-800 bg-zinc-900/50 rounded-sm px-4 py-3">
      <div className="overline text-zinc-500">{label}</div>
      <div className={`font-display font-black text-2xl tracking-tight mt-1 ${color}`}>{value}</div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="urlscans-page">
      <div>
        <div className="overline mb-2">Seguridad · Histórico</div>
        <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">URLs escaneadas</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Cada link que el bot analizó (manual con <code className="text-zinc-400">/escanear</code> o
          automático cuando alguien pega un link en chat).
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total" value={counts.total || 0} color="text-zinc-100" />
        <Stat label="Maliciosos" value={counts.malicious || 0} color="text-red-400" />
        <Stat label="Sospechosos" value={counts.suspicious || 0} color="text-amber-400" />
        <Stat label="Seguros" value={counts.safe || 0} color="text-emerald-400" />
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Buscar por URL, usuario o ID de Telegram"
          data-testid="urlscan-search-input"
          className="bg-zinc-950 border-zinc-800 rounded-sm max-w-md"
        />
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm max-w-[180px]" data-testid="urlscan-level-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectItem value="all">Todos los niveles</SelectItem>
            <SelectItem value="malicious">Malicioso</SelectItem>
            <SelectItem value="suspicious">Sospechoso</SelectItem>
            <SelectItem value="safe">Seguro</SelectItem>
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm max-w-[180px]" data-testid="urlscan-source-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectItem value="all">Todas las fuentes</SelectItem>
            <SelectItem value="manual">Manual (/escanear)</SelectItem>
            <SelectItem value="auto">Automático (chat)</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={onClearAll}
          data-testid="urlscan-clear-all-btn"
          className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm"
        >
          <Trash2 size={14} className="mr-1.5" />
          Limpiar {level === "all" ? "todo" : level}
        </Button>
      </div>

      <div className="border border-zinc-800 rounded-sm overflow-hidden bg-zinc-900/50">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-black/40">
                <th className="text-left px-4 py-3 overline">Fecha</th>
                <th className="text-left px-4 py-3 overline">Usuario</th>
                <th className="text-left px-4 py-3 overline">URL</th>
                <th className="text-left px-4 py-3 overline">Nivel</th>
                <th className="text-left px-4 py-3 overline">Amenaza</th>
                <th className="text-left px-4 py-3 overline">Fuente</th>
                <th className="text-left px-4 py-3 overline">Razones</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-10 text-center text-zinc-500">
                    No hay escaneos registrados con estos filtros.
                  </td>
                </tr>
              )}
              {items.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-zinc-900 hover:bg-zinc-900/40 align-top"
                  data-testid={`urlscan-row-${s.id}`}
                >
                  <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">
                    {new Date(s.created_at).toLocaleString("es-MX")}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {s.username ? `@${s.username}` : <span className="text-zinc-600">—</span>}
                    <div className="text-[10px] text-zinc-600 font-mono">{s.telegram_id}</div>
                  </td>
                  <td className="px-4 py-3 max-w-[360px]">
                    <div className="flex items-center gap-1.5">
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs text-[#2AABEE] hover:underline truncate inline-block max-w-[320px]"
                        title={s.url}
                      >
                        {s.url}
                      </a>
                      <ExternalLink size={11} className="text-zinc-600 flex-shrink-0" />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <LevelBadge level={s.level} />
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-zinc-400">{s.threat || "—"}</td>
                  <td className="px-4 py-3 text-[10px] font-mono uppercase text-zinc-500">
                    {s.source === "manual" ? "comando" : "auto"}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400 max-w-[340px]">
                    {(s.reasons || []).slice(0, 2).map((r, i) => (
                      <div key={`${s.id || s._id || s.scanned_at}-reason-${i}`} className="truncate" title={r}>
                        • {r}
                      </div>
                    ))}
                    {s.reasons && s.reasons.length > 2 && (
                      <div className="text-[10px] text-zinc-600">
                        + {s.reasons.length - 2} más
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(s.id)}
                      data-testid={`urlscan-delete-${s.id}`}
                      className="h-8 w-8 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-sm"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-zinc-600">
        Mostrando {items.length} de {total} registros (filtrados).
      </div>
    </div>
  );
}
